import { Link } from 'react-router-dom';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="max-w-6xl mx-auto px-2 sm:px-4 py-2 sm:py-4">
          <div className="flex items-center justify-between min-w-0">
            <Link to="/" className="flex items-center space-x-1 sm:space-x-2 hover:opacity-80 transition-opacity flex-shrink-0">
              <img src="/apple-touch-icon.png" alt="EightyThreeLabs" className="w-6 h-6 sm:w-8 sm:h-8" />
              <span className="text-sm sm:text-xl font-semibold">
                <span className="text-brand-primary">EightyThree</span>
                <span className="text-black dark:text-white">Labs</span>
              </span>
            </Link>
            
            {/* Auth Navigation */}
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              <Link 
                to="/login" 
                className="text-xs sm:text-base text-gray-600 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white font-medium transition-colors px-2 sm:px-0"
              >
                <span className="hidden sm:inline">Sign In</span>
                <span className="sm:hidden">Login</span>
              </Link>
              <Link 
                to="/signup" 
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 sm:px-6 py-1.5 sm:py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium shadow-md text-xs sm:text-base whitespace-nowrap"
              >
                <span className="hidden sm:inline">Get Started</span>
                <span className="sm:hidden">Start</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="max-w-5xl mx-auto px-4 py-16">
        <div className="text-center">
          <div className="inline-flex items-center bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
            🎯 Elite Mathematical Challenges
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Master Math Through
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> Interactive Challenges</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-200 mb-8 max-w-3xl mx-auto font-light">
            Challenge yourself with engaging mathematical puzzles, games, and analytics that make learning addictive.
          </p>
          
          {/* Pricing Preview */}
          <div className="bg-gradient-to-r from-blue-100 via-purple-100 to-indigo-100 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-8 mb-8 max-w-2xl mx-auto border border-blue-200/50 dark:border-blue-700/30 shadow-lg">
            <div className="text-center">
              <div className="inline-flex items-center bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm px-4 py-2 rounded-full mb-3">
                <span className="text-2xl mr-2">🎯</span>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Elite Access</span>
              </div>
              <p className="text-2xl text-gray-800 dark:text-gray-200 mb-2">
                <span className="font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Just $29.99/year</span>
              </p>
              <p className="text-base text-gray-600 dark:text-gray-300 font-medium">
                That's less than $2.50 per month • Cancel anytime
              </p>
            </div>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link 
              to="/signup" 
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              🚀 Start Learning Today
            </Link>
            <Link 
              to="/demo" 
              className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white border-2 border-gray-200 dark:border-gray-700 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              🎮 Try Demo First
            </Link>
          </div>

          {/* Social Proof */}
          <div className="flex items-center justify-center gap-8 text-sm text-gray-500 dark:text-gray-300">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full border-2 border-white"></div>
                <div className="w-8 h-8 bg-green-500 rounded-full border-2 border-white"></div>
                <div className="w-8 h-8 bg-purple-500 rounded-full border-2 border-white"></div>
              </div>
              <span>Hundreds of Active Learners</span>
            </div>
            <div className="flex items-center gap-1">
              <span>⭐⭐⭐⭐⭐</span>
              <span>4.9/5 Rating</span>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20 mt-20">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">🎮</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Interactive Games</h3>
              <p className="text-gray-600 dark:text-gray-200">
                Engage with math through fun, interactive games and daily challenges that make learning addictive.
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">📈</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Structured Learning</h3>
              <p className="text-gray-600 dark:text-gray-200">
                Follow our carefully designed curriculum that adapts to your pace and skill level.
              </p>
              <div className="mt-4 text-sm text-purple-600 dark:text-purple-400 font-medium">
                Coming Soon!
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl">📊</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Track Progress</h3>
              <p className="text-gray-600 dark:text-gray-200">
                Monitor your improvement with detailed analytics and achievement systems.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-12 shadow-xl max-w-2xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-200">
              One plan. All features. No complexity.
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-8 border-2 border-blue-200 dark:border-blue-700 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                  Elite Access
                </span>
              </div>
              
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">EightyThreeLabs - Elite Subscription</h3>
                <div className="text-4xl font-bold text-gray-900 dark:text-white mb-1">$29.99</div>
                <div className="text-gray-600 dark:text-gray-200 mb-6">per year</div>
                
                <ul className="space-y-3 mb-8 text-left">
                  <li className="flex items-center text-gray-700 dark:text-gray-200">
                    <span className="text-green-500 mr-3">✓</span>
                    Unlimited access to all mathematical challenges
                  </li>
                  <li className="flex items-center text-gray-700 dark:text-gray-200">
                    <span className="text-green-500 mr-3">✓</span>
                    Advanced progress tracking and analytics
                  </li>
                  <li className="flex items-center text-gray-700 dark:text-gray-200">
                    <span className="text-green-500 mr-3">✓</span>
                    Priority customer support
                  </li>
                  <li className="flex items-center text-gray-700 dark:text-gray-200">
                    <span className="text-green-500 mr-3">✓</span>
                    Early access to new features
                  </li>
                  <li className="flex items-center text-gray-700 dark:text-gray-200">
                    <span className="text-green-500 mr-3">✓</span>
                    Ad-free experience
                  </li>
                </ul>
                
                <Link 
                  to="/signup"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-xl font-bold text-center block hover:from-blue-700 hover:to-purple-700 transition-all"
                >
                  Get Elite Access
                </Link>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-4">
            <p className="text-gray-600 mb-2">
              Made by The <span className="text-brand-primary font-semibold">EightyThree</span><span className="font-semibold">Labs</span> Team
            </p>
          </div>
          
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
            <Link 
              to="/about" 
              className="text-gray-600 hover:text-brand-primary transition-colors"
            >
              About Us
            </Link>
            <Link 
              to="/privacy-policy" 
              className="text-gray-600 hover:text-brand-primary transition-colors"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms-of-service" 
              className="text-gray-600 hover:text-brand-primary transition-colors"
            >
              Terms of Service
            </Link>
            <Link 
              to="/cookie-policy" 
              className="text-gray-600 hover:text-brand-primary transition-colors"
            >
              Cookie Policy
            </Link>
          </div>
          
          <div className="text-center">
            <p className="text-xs text-gray-500">
              © 2025 EightyThreeLabs. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;